﻿import { uniq } from 'es-toolkit';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
  AdditionalLoads,
  AnalogJoint,
  AnalogLink,
  AnalogModel,
  AnalysisHistoryRecord,
  Bearing,
  CalculatedLine,
  Component,
  ComponentDesign,
  ComponentDesignTimestamps,
  ComponentType,
  Gusset,
  isTruss,
  MaterialSelection,
  Member,
  PlatePair,
  UserDistanceDimension,
} from '@paragon/contract-library';
import { RegenerateTrussFromSettingsResponse } from '@paragon/data-connections';
import {
  allComponentDesignsUnloaded,
  componentDesignsBatchLocked,
  componentDesignsDeletedWithInsertions,
} from '@paragon/layout-models';

import assertNotNullish from '../../library/utilities/assertNotNullish';
import pushUniqByGuid from '../../library/utilities/pushUniqByGuid';
import { LockOptions } from '../actions/lockOptionsActions';
import { redoComponentDesign, undoComponentDesign } from './editorSlice';

export interface ComponentDesignLoadedState {
  readonly committed: boolean;
  readonly fullyLoaded: boolean;
}

export interface ComponentDesignsState {
  readonly data: { readonly [componentDesignGuid: string]: ComponentDesign };

  /**
   * It is expected that each component design should have a corresponding `loadedState`.
   */
  readonly loadedState: { readonly [componentDesignGuid: string]: ComponentDesignLoadedState };
}

const initialState: ComponentDesignsState = { data: {}, loadedState: {} };

const componentDesignsSlice = createSlice({
  name: 'componentDesigns',
  initialState,
  reducers: {
    setComponentDesignWithoutInvalidation: (
      state,
      action: PayloadAction<{ componentDesign: ComponentDesign; loadedState?: ComponentDesignLoadedState }>,
    ) => {
      const { componentDesign, loadedState } = action.payload;

      state.data[componentDesign.guid] = castDraft(componentDesign);

      if (loadedState) {
        state.loadedState[componentDesign.guid] = loadedState;
      } else {
        if (!(componentDesign.guid in state.loadedState)) throw new Error('Expected loaded state to be set.');
      }
    },
    receiveComponentDesigns: (
      state,
      action: PayloadAction<{
        componentDesigns: readonly ComponentDesign[];
        timestamps: readonly ComponentDesignTimestamps[];
        history: readonly AnalysisHistoryRecord[];
      }>,
    ) => {
      for (const componentDesign of action.payload.componentDesigns) {
        state.data[componentDesign.guid] = castDraft(componentDesign);
        state.loadedState[componentDesign.guid] = { committed: true, fullyLoaded: false };
      }
    },
    setUnsavedComponentDesignFromServer: (
      state,
      action: PayloadAction<{ componentDesign: ComponentDesign; componentDesignGuid: string }>,
    ) => {
      const { componentDesign, componentDesignGuid } = action.payload;
      state.data[componentDesignGuid] = castDraft(componentDesign);
      if (!(componentDesignGuid in state.loadedState)) throw new Error('Expected loaded state to be set.');
    },
    // This action behaves (mostly) identically to setUnsavedComponentDesignFromServer, except that it allows overriding
    //   the lastUpdatedTimestamp and will not re-save the component design that's sent back from the server.  It should
    //   only be used to set the unsaved component design if we are reverting back to the most recent analysis set.
    setUnsavedComponentDesignFromMostRecentAnalysisSet: (
      state,
      action: PayloadAction<{
        componentDesignGuid: string;
        componentDesign: ComponentDesign;
        lastUpdatedTimestamp: string;
      }>,
    ) => {
      const { componentDesign, componentDesignGuid } = action.payload;
      state.data[componentDesignGuid] = castDraft(componentDesign);
      if (!(componentDesignGuid in state.loadedState)) throw new Error('Expected loaded state to be set.');
    },
    receiveRegenerateFromSettingsResponse: (state, action: PayloadAction<RegenerateTrussFromSettingsResponse>) => {
      const { componentDesign } = action.payload;
      state.data[componentDesign.guid] = castDraft(componentDesign);
      if (!(componentDesign.guid in state.loadedState)) throw new Error('Expected loaded state to be set.');
    },
    markComponentDesignAsFullyLoaded: (state, action: PayloadAction<string>) => {
      const componentDesignGuid = action.payload;
      if (!(componentDesignGuid in state.loadedState)) throw new Error('Expected loaded state to be set.');
      state.loadedState[componentDesignGuid].fullyLoaded = true;
    },
    setMaterialSelection: (
      state,
      action: PayloadAction<{
        readonly componentDesignGuid: string;
        readonly materialSelection: MaterialSelection;
      }>,
    ) => {
      const { componentDesignGuid, materialSelection } = action.payload;
      setMaterialSelectionWithInvalidation(state.data[componentDesignGuid], materialSelection);
    },
    setUserDistanceDimensions: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; userDistanceDimensions: readonly UserDistanceDimension[] }>,
    ) => {
      const { componentDesignGuid, userDistanceDimensions } = action.payload;
      state.data[componentDesignGuid].userDistanceDimensions = castDraft(userDistanceDimensions);
    },
    setAdditionalLoads: (
      state,
      action: PayloadAction<{ additionalLoads: AdditionalLoads; componentDesignGuid: string }>,
    ) => {
      const { additionalLoads, componentDesignGuid } = action.payload;
      setAdditionalLoadsWithInvalidation(state.data[componentDesignGuid], additionalLoads);
    },
    setAnalogModel: (state, action: PayloadAction<{ analogModel: AnalogModel; componentDesignGuid: string }>) => {
      const { analogModel, componentDesignGuid } = action.payload;
      setAnalogModelWithInvalidation(state.data[componentDesignGuid], analogModel);
    },
    setAnalogJoint: (
      state,
      action: PayloadAction<{ joint: AnalogJoint; componentDesignGuid: string; lockAnalog?: boolean }>,
    ) => {
      const { joint, componentDesignGuid, lockAnalog } = action.payload;
      const componentDesign = state.data[componentDesignGuid];
      const { analogModel } = componentDesign;
      if (!assertNotNullish(analogModel)) return;

      const newAnalogModel = { ...analogModel, analogJoints: pushUniqByGuid(analogModel.analogJoints, joint) };
      // update analog link joints
      for (const analogLink of analogModel.analogLinks) {
        if (analogLink.iJoint.guid === joint.guid) {
          const newLink = { ...analogLink };
          newLink.iJoint = { ...joint };
          newAnalogModel.analogLinks = pushUniqByGuid(newAnalogModel.analogLinks, newLink);
        } else if (analogLink.jJoint.guid === joint.guid) {
          const newLink = { ...analogLink };
          newLink.jJoint = { ...joint };
          newAnalogModel.analogLinks = pushUniqByGuid(newAnalogModel.analogLinks, newLink);
        }
      }

      if (lockAnalog != null) componentDesign.analogLocked = lockAnalog;
      setAnalogModelWithInvalidation(componentDesign, newAnalogModel);
    },
    setAnalogLink: (
      state,
      action: PayloadAction<{ link: AnalogLink; componentDesignGuid: string; lockAnalog?: boolean }>,
    ) => {
      const { link, componentDesignGuid, lockAnalog } = action.payload;
      const componentDesign = state.data[componentDesignGuid];
      const { analogModel } = componentDesign;
      if (!assertNotNullish(analogModel)) return;

      const newAnalogModel = { ...analogModel, analogLinks: pushUniqByGuid(analogModel.analogLinks, link) };

      if (lockAnalog != null) componentDesign.analogLocked = lockAnalog;
      setAnalogModelWithInvalidation(componentDesign, newAnalogModel);
    },
    setComponent: (
      state,
      action: PayloadAction<{ component: Component; componentDesignGuid: string; skipInvalidation?: boolean }>,
    ) => {
      const { component, componentDesignGuid, skipInvalidation } = action.payload;
      const componentDesign = state.data[componentDesignGuid];
      // FIXME Calling `castDraft` on `component` and then passing it to functions that mutate the `component` is not
      //   safe because we shouldn't be mutating the component directly.
      componentDesign.component = castDraft({ ...component });
      if (!skipInvalidation) invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    setPlatePairs: (state, action: PayloadAction<{ platePairs: PlatePair[]; componentDesignGuid: string }>) => {
      const { platePairs, componentDesignGuid } = action.payload;
      setPlatePairsInComponentDesign(state.data[componentDesignGuid], platePairs);
    },
    setBearing: (state, action: PayloadAction<{ bearing: Bearing; componentDesignGuid: string }>) => {
      const { bearing, componentDesignGuid } = action.payload;
      const componentDesign = state.data[componentDesignGuid];
      componentDesign.component.bearings = castDraft(pushUniqByGuid(componentDesign.component.bearings, bearing));
      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    upsertPlatePairs: (
      state,
      action: PayloadAction<{ platePairs: PlatePair[]; componentDesignGuid: string; lockPlating?: boolean }>,
    ) => {
      const { platePairs, componentDesignGuid, lockPlating } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      for (const platePair of platePairs) {
        componentDesign.component.platePairs = castDraft(
          pushUniqByGuid(componentDesign.component.platePairs, platePair),
        );
      }

      if (!componentDesign.platingLocked && lockPlating) {
        componentDesign.lockedPlatePairs = uniq([
          ...componentDesign.lockedPlatePairs,
          ...platePairs.map((platePair) => platePair.guid),
        ]);
      }
    },
    setMembers: (
      state,
      action: PayloadAction<{ members: Member[]; componentDesignGuid: string; lockMembers?: boolean }>,
    ) => {
      const { members, componentDesignGuid, lockMembers } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      for (const member of members) {
        setMemberInComponentDesign(componentDesign, member, lockMembers ?? false);
      }

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    setReferenceLine: (
      state,
      action: PayloadAction<{ referenceLine: CalculatedLine; componentDesignGuid: string }>,
    ) => {
      const { referenceLine, componentDesignGuid } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      setReferenceLineInComponentDesign(componentDesign, referenceLine);

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    setReferenceLines: (
      state,
      action: PayloadAction<{ referenceLines: readonly CalculatedLine[]; componentDesignGuid: string }>,
    ) => {
      const { referenceLines, componentDesignGuid } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      for (const referenceLine of referenceLines) {
        setReferenceLineInComponentDesign(componentDesign, referenceLine);
      }

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    setDimensionSetGuid: (state, action: PayloadAction<{ dimensionSetGuid: string; componentDesignGuid: string }>) => {
      const { componentDesignGuid, dimensionSetGuid } = action.payload;
      state.data[componentDesignGuid].dimensionSetGuid = dimensionSetGuid;
    },
    setLockOptions: (
      state,
      action: PayloadAction<{
        componentDesignGuid: string;
        lockOptions: LockOptions;

        // If set, and hangersLocked, membersLocked, or platingLocked is false, all individual corresponding items will be unlocked.
        forceIndividualUnlock?: boolean;
      }>,
    ) => {
      const { componentDesignGuid, lockOptions, forceIndividualUnlock } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      const hangerLockingToggled = componentDesign.hangersLocked !== lockOptions.hangersLocked;
      const memberLockingToggled = componentDesign.membersLocked !== lockOptions.membersLocked;
      const platePairLockingToggled = componentDesign.platingLocked !== lockOptions.platingLocked;

      for (const [lockOptionKey, lockOptionValue] of Object.entries(lockOptions)) {
        componentDesign[lockOptionKey as keyof LockOptions] = lockOptionValue;
      }

      if (hangerLockingToggled || (!lockOptions.hangersLocked && forceIndividualUnlock)) {
        componentDesign.lockedHangers = [];
      }

      if (memberLockingToggled || (!lockOptions.membersLocked && forceIndividualUnlock)) {
        componentDesign.lockedMembers = [];
      }

      if (platePairLockingToggled || (!lockOptions.platingLocked && forceIndividualUnlock)) {
        componentDesign.lockedPlatePairs = [];
      }
    },
    setLockedHangers: (state, action: PayloadAction<{ componentDesignGuid: string; lockedHangers: string[] }>) => {
      const { componentDesignGuid, lockedHangers } = action.payload;
      state.data[componentDesignGuid].lockedHangers = lockedHangers;
    },
    setLockedMembers: (state, action: PayloadAction<{ componentDesignGuid: string; lockedMembers: string[] }>) => {
      const { componentDesignGuid, lockedMembers } = action.payload;
      state.data[componentDesignGuid].lockedMembers = lockedMembers;
    },
    setLockedPlatePairs: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; lockedPlatePairs: string[] }>,
    ) => {
      const { componentDesignGuid, lockedPlatePairs } = action.payload;
      state.data[componentDesignGuid].lockedPlatePairs = lockedPlatePairs;
    },
    setComponentPlies: (state, action: PayloadAction<{ componentDesignGuid: string; plies: number }>) => {
      const { componentDesignGuid, plies } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      if (componentDesign.component.componentType === 'Wall') return;

      componentDesign.component.numberOfPlies = plies;
      if (componentDesign.profile) componentDesign.profile.plies = plies;
    },
    setComponentSpacing: (state, action: PayloadAction<{ componentDesignGuid: string; spacing: number }>) => {
      const { componentDesignGuid, spacing } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      if (componentDesign.component.componentType === 'Wall') return;

      componentDesign.component.spacing = spacing;
      if (componentDesign.profile) componentDesign.profile.spacing = spacing;
    },
    setComponentType: (state, action: PayloadAction<{ componentDesignGuid: string; componentType: ComponentType }>) => {
      const { componentDesignGuid, componentType } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      componentDesign.component.componentType = componentType;

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    commitMembers: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; referenceLines: CalculatedLine[]; members: Member[] }>,
    ) => {
      const { componentDesignGuid, referenceLines, members } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      for (const referenceLine of referenceLines) {
        setReferenceLineInComponentDesign(componentDesign, referenceLine);
      }

      for (const member of members) {
        setMemberInComponentDesign(componentDesign, member, false);
      }

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    setMemberName: (state, action: PayloadAction<{ componentDesignGuid: string; member: Member; name: string }>) => {
      const { componentDesignGuid, member, name } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      setMemberInComponentDesign(componentDesign, { ...member, name }, false);
      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    commitBearing: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; bearing: Bearing; referenceLines: CalculatedLine[] }>,
    ) => {
      const { componentDesignGuid, bearing, referenceLines: memberLines } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      for (const memberLine of memberLines) {
        setReferenceLineInComponentDesign(componentDesign, memberLine);
      }
      componentDesign.component.bearings = castDraft(pushUniqByGuid(componentDesign.component.bearings, bearing));

      invalidateComponentDesignForUpdatedComponent(componentDesign);
    },
    updateGusset: (state, action: PayloadAction<{ componentDesignGuid: string; gusset: Gusset }>) => {
      const { componentDesignGuid, gusset } = action.payload;
      const componentDesign = state.data[componentDesignGuid];

      componentDesign.component.gussets = castDraft(pushUniqByGuid(componentDesign.component.gussets, gusset));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsBatchLocked, (state, action) => {
      const { lockTargets } = action.payload;

      // Component designs were modified on the server. Apply the same changes here.
      for (const componentDesign of Object.values(state.data)) {
        const updatedSummary = action.payload.updatedSummariesAndTimestamps.componentDesignSummaries.find(
          (summary) => summary.guid === componentDesign.guid,
        );
        if (!updatedSummary) continue;

        componentDesign.analogLocked = updatedSummary.analogLocked;
        componentDesign.hangersLocked = updatedSummary.hangersLocked;
        componentDesign.loadingLocked = updatedSummary.loadingLocked;
        componentDesign.membersLocked = updatedSummary.membersLocked;
        componentDesign.platingLocked = updatedSummary.platingLocked;

        if (lockTargets.includes('hangers')) componentDesign.lockedHangers = [];
        if (lockTargets.includes('members')) componentDesign.lockedMembers = [];
        if (lockTargets.includes('plating')) componentDesign.lockedPlatePairs = [];
      }
    });

    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const componentDesignGuid of action.payload.guids) {
        delete state.data[componentDesignGuid];
        delete state.loadedState[componentDesignGuid];
      }
    });

    builder.addCase(allComponentDesignsUnloaded, () => initialState);

    builder.addCase(undoComponentDesign, (state, action) => {
      state.data[action.payload.target.guid] = castDraft(action.payload.target);
    });

    builder.addCase(redoComponentDesign, (state, action) => {
      state.data[action.payload.target.guid] = castDraft(action.payload.target);
    });
  },
});

function invalidateComponentDesignForUpdatedComponent(componentDesign: Draft<ComponentDesign>) {
  if (!componentDesign.analogLocked) setAnalogModelWithInvalidation(componentDesign, null);
}

function setAnalogModelWithInvalidation(componentDesign: Draft<ComponentDesign>, analogModel: AnalogModel | null) {
  if (componentDesign.analogLocked) console.warn('Analog is locked but is being updated.');

  componentDesign.analogModel = castDraft(analogModel);
  // Unlock analog since it's being invalidated
  if (!analogModel) componentDesign.analogLocked = false;

  if (!componentDesign.loadingLocked) setLoadSetWithInvalidation(componentDesign, null);
}

function setAdditionalLoadsWithInvalidation(componentDesign: Draft<ComponentDesign>, additionalLoads: AdditionalLoads) {
  componentDesign.additionalLoads = castDraft(additionalLoads);
  setLoadSetWithInvalidation(componentDesign, null);
}

function setLoadSetWithInvalidation(componentDesign: Draft<ComponentDesign>, loadSetGuid: string | null) {
  if (componentDesign.loadingLocked) console.warn('Loading is locked but is being updated.');

  componentDesign.loadSetGuid = loadSetGuid;
  // Unlock loading since it's being invalidated
  if (loadSetGuid === null) componentDesign.loadingLocked = false;
  componentDesign.loadingLocked = loadSetGuid === null ? false : componentDesign.loadingLocked;

  if (!componentDesign.platingLocked) {
    setPlatePairsInComponentDesign(
      componentDesign,
      componentDesign.component.platePairs.filter((platePair) =>
        componentDesign.lockedPlatePairs.includes(platePair.guid),
      ),
    );
  }
}

function setMaterialSelectionWithInvalidation(
  componentDesign: Draft<ComponentDesign>,
  materialSelection: MaterialSelection,
) {
  componentDesign.materialSelection = materialSelection;

  if (!componentDesign.platingLocked) {
    setPlatePairsInComponentDesign(
      componentDesign,
      componentDesign.component.platePairs.filter((platePair) =>
        componentDesign.lockedPlatePairs.includes(platePair.guid),
      ),
    );
  }
}

function setReferenceLineInComponentDesign(componentDesign: Draft<ComponentDesign>, referenceLine: CalculatedLine) {
  if (!componentDesign.component.referenceLines) throw new Error('component.referenceLines is null.');
  componentDesign.component.referenceLines = pushUniqByGuid(componentDesign.component.referenceLines, referenceLine);
}

export function setMemberInComponentDesign(
  componentDesign: Draft<ComponentDesign>,
  member: Member,
  lockMember: boolean,
) {
  if (member.relativeGeometry?.isBrace) {
    if (!isTruss(componentDesign.component)) throw new Error('Unexpected state');
    componentDesign.component.braceMembers = castDraft(pushUniqByGuid(componentDesign.component.braceMembers, member));
  } else {
    componentDesign.component.members = castDraft(pushUniqByGuid(componentDesign.component.members, member));
  }

  if (!componentDesign.membersLocked && lockMember) {
    componentDesign.lockedMembers = uniq([...componentDesign.lockedMembers, member.guid]);
  }
}

function setPlatePairsInComponentDesign(componentDesign: Draft<ComponentDesign>, platePairs: readonly PlatePair[]) {
  if (componentDesign.platingLocked) console.warn('Plating is locked but is being updated.');
  componentDesign.component.platePairs = castDraft(platePairs);
  // Unlock plating if there are no plates now
  if (platePairs.length === 0) {
    componentDesign.platingLocked = false;
    componentDesign.lockedPlatePairs = [];
  }
}

export const {
  setComponentDesignWithoutInvalidation,
  receiveComponentDesigns,
  setUnsavedComponentDesignFromServer,
  setUnsavedComponentDesignFromMostRecentAnalysisSet,
  receiveRegenerateFromSettingsResponse,
  markComponentDesignAsFullyLoaded,
  setMaterialSelection,
  setUserDistanceDimensions,
  setAdditionalLoads,
  setAnalogModel,
  setAnalogJoint,
  setAnalogLink,
  setComponent,
  setPlatePairs,
  setBearing,
  upsertPlatePairs,
  setMembers,
  setReferenceLine,
  setReferenceLines,
  setDimensionSetGuid,
  setLockOptions,
  setLockedHangers,
  setLockedMembers,
  setLockedPlatePairs,
  setComponentPlies,
  setComponentSpacing,
  setComponentType,
  commitMembers,
  setMemberName,
  commitBearing,
  updateGusset,
} = componentDesignsSlice.actions;
export const componentDesigns = componentDesignsSlice.reducer;

/**
 * An array of actions that will trigger a component design to be automatically saved.
 */
export const saveActions = [
  setUnsavedComponentDesignFromServer,
  setUnsavedComponentDesignFromMostRecentAnalysisSet,
  setMaterialSelection,
  setUserDistanceDimensions,
  setAdditionalLoads,
  setAnalogModel,
  setAnalogJoint,
  setAnalogLink,
  setComponent,
  setPlatePairs,
  setBearing,
  upsertPlatePairs,
  setMembers,
  setReferenceLine,
  setReferenceLines,
  setLockOptions,
  setLockedHangers,
  setLockedMembers,
  setLockedPlatePairs,
  setComponentPlies,
  setComponentSpacing,
  setComponentType,
  commitMembers,
  setMemberName,
  commitBearing,
  updateGusset,

  undoComponentDesign,
  redoComponentDesign,
] as const;

type AllActions = {
  [K in keyof typeof componentDesignsSlice.actions]: (typeof componentDesignsSlice.actions)[K]['type'];
}[keyof typeof componentDesignsSlice.actions];

type SaveActions = (typeof saveActions)[number]['type'];

/**
 * The actions that don't cause a component design to be automatically saved. These are explicitly listed out so that we
 * don't miss any.
 */
type UnsavedActions =
  | (typeof setComponentDesignWithoutInvalidation)['type']
  | (typeof receiveComponentDesigns)['type']
  | (typeof receiveRegenerateFromSettingsResponse)['type']
  | (typeof markComponentDesignAsFullyLoaded)['type']
  | (typeof setDimensionSetGuid)['type'];

/**
 * This ensures that all actions are either in the `saveActions` array, or explicitly marked as an action that does not
 * cause a component design to get automatically saved. If you get a TypeScript error on the following line, then you
 * probably need to add an action to `saveActions` or `UnsavedActions`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emptyActions: Record<Exclude<AllActions, SaveActions | UnsavedActions>, string> = {};
